import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Checkout() {
  const { items, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const pay = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_name: name,
        shipping_address: address,
        email: email,
      });
      window.location.href = data.url;
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Error al iniciar el pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl text-stone-900">No hay productos para pagar</h1>
        <button onClick={() => navigate("/shop")} className="mt-6 bg-[#3F4E4F] text-white px-8 py-3">Ir a la tienda</button>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-12" data-testid="checkout-page">
      <h1 className="font-serif text-4xl md:text-5xl text-stone-900 mb-10">Finalizar compra</h1>
      <div className="grid lg:grid-cols-2 gap-12">
        <form onSubmit={pay} className="space-y-5">
          <h2 className="font-serif text-2xl text-stone-900">Datos de envío</h2>
          <div>
            <label className="label-caps text-stone-500 block mb-2">Nombre completo</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-stone-300 px-4 py-3 outline-none focus:border-[#3F4E4F]" data-testid="shipping-name-input" />
          </div>
          <div>
            <label className="label-caps text-stone-500 block mb-2">Correo electrónico</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Para enviarle la confirmación de su pedido" className="w-full border border-stone-300 px-4 py-3 outline-none focus:border-[#3F4E4F]" data-testid="checkout-email-input" />
          </div>
          <div>
            <label className="label-caps text-stone-500 block mb-2">Dirección de envío</label>
            <textarea required value={address} onChange={(e) => setAddress(e.target.value)} rows={3} className="w-full border border-stone-300 px-4 py-3 outline-none focus:border-[#3F4E4F]" data-testid="shipping-address-input" />
          </div>
          {error && <div className="text-[#991B1B] text-sm" data-testid="checkout-error">{error}</div>}
          <button type="submit" disabled={loading} className="w-full bg-[#3F4E4F] text-white py-4 hover:bg-[#2C3637] transition-colors disabled:opacity-50" data-testid="pay-button">
            {loading ? "Redirigiendo a pago seguro..." : `Pagar $${total.toFixed(2)}`}
          </button>
          <p className="text-xs text-stone-400 text-center">Pago seguro procesado por Stripe.</p>
        </form>

        <div className="bg-white border border-stone-200 p-8 h-fit" data-testid="checkout-summary">
          <h2 className="font-serif text-2xl text-stone-900 mb-6">Tu pedido</h2>
          <div className="space-y-4 mb-6">
            {items.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span className="text-stone-600">{i.name} × {i.quantity}</span>
                <span className="text-stone-900">${(i.unit_price * i.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-200 pt-4 flex justify-between text-lg font-medium">
            <span>Total</span><span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
